import { useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Search, Check } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface Recipient {
  _id: string;
  name: string;
  email: string;
  role: string;
  company: string | null;
}

type Scope = 'account' | 'supervisor';
type Source = 'am' | 'house';

interface Props {
  /** What is being tagged. 'account' targets one clinic, 'supervisor' everything under a supervisor. */
  scope: Scope;
  targetId: string;
  targetName?: string;
  onClose: () => void;
  onCreated: () => void;
}

// An account tag can only draw on the AM's commission; a supervisor tag can only
// draw on the house. Keeping the pairing here rather than offering a free choice
// means the copy below can state one unambiguous basis instead of hedging.
const SOURCE_BY_SCOPE: Record<Scope, Source> = {
  account: 'am',
  supervisor: 'house'
};

const COPY: Record<Scope, { basisLabel: string; pocket: string; ratePlaceholder: string }> = {
  account: {
    basisLabel: 'Rate (% of AM commission)',
    pocket: "This cut comes out of the assigned AM's commission.",
    ratePlaceholder: 'e.g. 20'
  },
  supervisor: {
    basisLabel: 'Rate (% of floor-catalog price)',
    pocket:
      'This cut comes out of the house margin — not any AM\'s commission — and applies to every ' +
      'clinic under this supervisor, including ones added later.',
    ratePlaceholder: 'e.g. 20'
  }
};

/**
 * Add a commission override to one target. See the shared CommissionOverride type
 * for the contract: `kind` is the lifecycle (permanent lead-source vs temporary
 * ramp-down), `scope` is what's tagged, and the resulting `source` is whose margin
 * pays for it.
 */
const roleLabel = (role: string) => (role === 'distributor' ? 'AM' : role);

export default function CommissionOverrideModal({ scope, targetId, targetName, onClose, onCreated }: Props) {
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  // The chosen recipient's full record, captured at selection time so the summary
  // survives the list being replaced by a later search — the selected user may not
  // appear in the current server results.
  const [selectedRecipient, setSelectedRecipient] = useState<Recipient | null>(null);
  const [kind, setKind] = useState<'permanent' | 'temporary'>('permanent');
  const [rate, setRate] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const recipientBoxRef = useRef<HTMLDivElement>(null);

  const source = SOURCE_BY_SCOPE[scope];
  const copy = COPY[scope];
  const recipientId = selectedRecipient?._id || '';

  // Recipient loading is search-driven and SERVER-side: debounce the box and
  // re-query, so anyone past the first page is reachable by typing. Filtering a
  // single fetched page in-browser would silently hide everyone beyond the cap.
  useEffect(() => {
    let cancelled = false;
    setRecipientsLoading(true);
    const handle = setTimeout(async () => {
      const res = await apiClient.getCommissionOverrideRecipients(recipientSearch.trim() || undefined);
      if (cancelled) return;
      setRecipientsLoading(false);
      if (res.success) setRecipients(res.data?.recipients || (res as any).recipients || []);
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [recipientSearch]);

  // Close the recipient list on an outside click, so it can't sit open over the
  // rest of the form.
  useEffect(() => {
    if (!showRecipients) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!recipientBoxRef.current?.contains(e.target as Node)) setShowRecipients(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showRecipients]);

  const rateNum = useMemo(() => parseFloat(rate), [rate]);
  const valid =
    !!recipientId &&
    rateNum > 0 && rateNum <= 100 &&
    (kind === 'permanent' || (!!endDate && (!startDate || endDate > startDate)));

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    const res = await apiClient.createCommissionOverride({
      scope,
      source,
      ...(scope === 'supervisor' ? { supervisorId: targetId } : { accountId: targetId }),
      recipientId,
      kind,
      rate: rateNum,
      startDate: startDate || undefined,
      endDate: kind === 'temporary' ? endDate : undefined,
      note: note.trim() || undefined
    });
    setSubmitting(false);
    if (res.success) {
      toast.success('Override added');
      onCreated();
      onClose();
    } else {
      toast.error(res.error || 'Failed to add override');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Add {scope === 'supervisor' ? 'supervisor' : 'commission'} override
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-gray-500">
            {targetName && (
              <>
                On {scope === 'supervisor' ? 'supervisor' : 'account'}{' '}
                <span className="font-medium text-gray-800">{targetName}</span>.{' '}
              </>
            )}
            {copy.pocket}
          </p>

          <div ref={recipientBoxRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient</label>

            {selectedRecipient && !showRecipients ? (
              <button
                type="button"
                onClick={() => { setShowRecipients(true); setRecipientSearch(''); }}
                className="w-full flex items-center justify-between border border-gray-300 rounded-md px-3 py-2 text-sm text-left hover:border-gray-400"
              >
                <span className="min-w-0">
                  <span className="block truncate text-gray-900">{selectedRecipient.name}</span>
                  <span className="block truncate text-xs text-gray-400">
                    {roleLabel(selectedRecipient.role)}
                    {selectedRecipient.company ? ` · ${selectedRecipient.company}` : ''}
                    {selectedRecipient.email ? ` · ${selectedRecipient.email}` : ''}
                  </span>
                </span>
                <span className="text-xs text-indigo-600 flex-shrink-0 ml-2">Change</span>
              </button>
            ) : (
              <>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    autoFocus={showRecipients}
                    value={recipientSearch}
                    onChange={e => setRecipientSearch(e.target.value)}
                    onFocus={() => setShowRecipients(true)}
                    placeholder="Search by name, email, or company…"
                    className="w-full border border-gray-300 rounded-md pl-8 pr-3 py-2 text-sm"
                  />
                </div>

                {showRecipients && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                    {recipientsLoading ? (
                      <p className="px-3 py-2 text-sm text-gray-400">Searching…</p>
                    ) : recipients.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-gray-400">
                        {recipientSearch.trim() ? 'No recipients match that search.' : 'No payable recipients yet.'}
                      </p>
                    ) : (
                      recipients.map(r => (
                        <button
                          key={r._id}
                          type="button"
                          onClick={() => { setSelectedRecipient(r); setShowRecipients(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-gray-900">{r.name}</span>
                            <span className="block truncate text-xs text-gray-400">
                              {roleLabel(r.role)}
                              {r.company ? ` · ${r.company}` : ''}
                              {r.email ? ` · ${r.email}` : ''}
                            </span>
                          </span>
                          {recipientId === r._id && <Check size={14} className="text-indigo-600 flex-shrink-0" />}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="flex gap-2">
              {(['permanent', 'temporary'] as const).map(k => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`flex-1 border rounded-md px-3 py-2 text-sm capitalize ${
                    kind === k ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 text-gray-600'
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {kind === 'permanent'
                ? `Lead-source cut — runs for the life of the ${scope === 'supervisor' ? 'supervisor' : 'account'}.`
                : `Ramp-down — ends on the date below, then reverts fully to ${
                    scope === 'supervisor' ? 'the house' : 'the AM'
                  }.`}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{copy.basisLabel}</label>
            <input
              type="number" min="0" max="100" step="0.5"
              value={rate}
              onChange={e => setRate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={copy.ratePlaceholder}
            />
            {scope === 'supervisor' && (
              <p className="text-xs text-gray-400 mt-1">
                Applied to the floor-catalog price of each line — what this supervisor buys at, before
                any markup. Not a share of the margin, so on a thin-margin product the payout can
                exceed what the house made on it.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
              <input
                type="date" value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Defaults to today.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End date {kind === 'temporary' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="date" value={endDate}
                disabled={kind === 'permanent'}
                onChange={e => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text" value={note}
              onChange={e => setNote(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. AcmeLeads partnership"
            />
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-md p-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <span>An admin is required to remove an override once added.</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!valid || submitting}
            className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add override'}
          </button>
        </div>
      </div>
    </div>
  );
}
